import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: ''
  });

  // URL'den register parametresini kontrol et
  useEffect(() => {
    if (searchParams.get('register') === 'true') {
      setIsRegistering(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (isRegistering) {
        // Kayıt işlemi - form validasyonu
        if (!formData.fullName.trim()) {
          setError('Ad Soyad alanı zorunludur');
          setLoading(false);
          return;
        }
        if (!formData.phone.trim()) {
          setError('Telefon numarası zorunludur');
          setLoading(false);
          return;
        }
        if (!validatePhone(formData.phone)) {
          setError('Geçerli bir telefon numarası giriniz (10-11 haneli)');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('Şifre en az 6 karakter olmalıdır');
          setLoading(false);
          return;
        }
        
        const { data, error } = await signUp(formData.email, formData.password, formData.fullName, formData.phone);
        
        if (error) {
          console.error('Signup error:', error);
          if (error.message.includes('User already registered')) {
            setError('❌ Bu e-posta adresi zaten kayıtlı. Giriş yapmayı deneyin veya farklı bir e-posta kullanın.');
          } else if (error.message.includes('Password should be at least 6 characters')) {
            setError('❌ Şifre en az 6 karakter olmalıdır');
          } else if (error.message.includes('Invalid email')) {
            setError('❌ Geçerli bir e-posta adresi giriniz');
          } else {
            setError('❌ Kayıt olurken bir hata oluştu. Lütfen tekrar deneyin.');
          }
        } else {
          setError('');
          // E-posta doğrulama mesajı
          setSuccessMessage('🎉 Kayıt başarılı! E-posta adresinize gönderilen doğrulama linkine tıklayarak hesabınızı aktifleştirin. Spam klasörünü de kontrol etmeyi unutmayın.');
          // Formu temizle
          setFormData({ email: '', password: '', fullName: '', phone: '' });
        }
      } else {
        // Giriş işlemi
        const { data, error } = await signIn(formData.email, formData.password);
        
        if (error) {
          console.error('Login error:', error);
          if (error.message.includes('Invalid login credentials')) {
            setError('❌ E-posta veya şifre hatalı!\n\n💡 Olası sebepler:\n• E-posta veya şifre yanlış girilmiş\n• E-posta doğrulaması yapılmamış (en yaygın sebep)\n• Hesap henüz oluşturulmamış\n\n✅ Çözüm:\n1. E-posta kutunuzu kontrol edin\n2. Spam/gereksiz klasörünü kontrol edin\n3. Doğrulama linkine tıklayın\n4. Ardından tekrar giriş yapmayı deneyin');
          } else if (error.message.includes('Email not confirmed')) {
            setError('⚠️ E-posta adresinizi henüz doğrulamadınız!\n\nLütfen e-posta kutunuzu kontrol edin ve doğrulama linkine tıklayın. Spam/gereksiz klasörünü de kontrol etmeyi unutmayın.');
          } else {
            setError('❌ Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.');
          }
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('❌ Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setError('Google ile giriş yapılırken bir hata oluştu');
      }
    } catch (err) {
      setError('Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[0-9]{10,11}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ''));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/10 backdrop-blur-md shadow-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <h1 className="text-2xl font-bold text-white">
                pad<span className="text-orange-500">baq</span>
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/')}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line mr-2"></i>
                Ana Sayfa
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Login Content */}
      <div className="relative z-10 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-64px)]">
        <div className="max-w-md w-full">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              pad<span className="text-orange-500">baq</span>
            </h1>
            <h2 className="text-2xl font-bold text-white mb-2">
              {isRegistering ? 'Hesap Oluştur' : 'Hoş Geldiniz'}
            </h2>
            <p className="text-white/70">
              {isRegistering 
                ? 'Güvenilir platform için detaylı bilgilerinizi alıyoruz' 
                : 'Hesabınıza giriş yaparak alanlarınızı yönetin'}
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white/10 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/10">
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-red-400 mt-0.5 mr-2 flex-shrink-0"></i>
                  <div>{error}</div>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-200 text-sm">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-400 mt-0.5 mr-2 flex-shrink-0"></i>
                  <div>{successMessage}</div>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              {isRegistering && (
                <>
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-white/90 mb-2">
                      Ad Soyad <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="ri-user-line text-white/50"></i>
                      </div>
                      <input
                        id="fullName"
                        name="fullName"
                        type="text"
                        required={isRegistering}
                        className="block w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-white placeholder-white/50 backdrop-blur-sm"
                        placeholder="Adınız Soyadınız"
                        value={formData.fullName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-white/90 mb-2">
                      Telefon Numarası <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <i className="ri-phone-line text-white/50"></i>
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required={isRegistering}
                        className="block w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-white placeholder-white/50 backdrop-blur-sm"
                        placeholder="0555 123 45 67"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    {isRegistering && formData.phone && !validatePhone(formData.phone) && (
                      <p className="mt-1 text-xs text-yellow-300">
                        <i className="ri-information-line mr-1"></i>
                        Geçerli bir telefon numarası giriniz (10-11 haneli)
                      </p>
                    )}
                  </div>
                </>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                  E-posta Adresi <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-white/50"></i>
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="block w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-white placeholder-white/50 backdrop-blur-sm"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white/90 mb-2">
                  Şifre <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-white/50"></i>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={6}
                    className="block w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-white placeholder-white/50 backdrop-blur-sm"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
                {isRegistering && (
                  <p className="mt-1 text-xs text-white/50">
                    <i className="ri-information-line mr-1"></i>
                    Şifreniz en az 6 karakter olmalıdır
                  </p>
                )}
              </div>

              {!isRegistering && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-white/30 rounded cursor-pointer bg-white/10"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-white/80 cursor-pointer">
                      Beni hatırla
                    </label>
                  </div>

                  <div className="text-sm">
                    <Link to="/forgot-password" className="font-medium text-orange-400 hover:text-orange-300 cursor-pointer transition-colors">
                      Şifremi unuttum
                    </Link>
                  </div>
                </div>
              )}

              {isRegistering && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <div className="flex items-start">
                    <i className="ri-shield-check-line text-blue-400 mt-0.5 mr-2 flex-shrink-0"></i>
                    <div className="text-xs text-blue-200">
                      <strong>Neden bu bilgileri topluyoruz?</strong>
                      <ul className="mt-1 space-y-1">
                        <li>• Alan sahipleriyle güvenli iletişim için</li>
                        <li>• Dolandırıcılık önleme ve güvenlik</li>
                        <li>• Acil durumda ulaşabilmek için</li>
                        <li>• Hukuki süreçlerde sorumluluk</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading || (isRegistering && (!formData.fullName || !formData.phone || !validatePhone(formData.phone)))}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <i className={`${isRegistering ? 'ri-user-add-line' : 'ri-login-circle-line'} mr-2`}></i>
                      {isRegistering ? 'Güvenli Kayıt Ol' : 'Giriş Yap'}
                    </>
                  )}
                </button>
              </div>

              <div className="text-center">
                <span className="text-sm text-white/70">
                  {isRegistering ? 'Zaten hesabınız var mı?' : 'Hesabınız yok mu?'}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsRegistering(!isRegistering);
                      setError('');
                      setSuccessMessage('');
                      setFormData({ email: '', password: '', fullName: '', phone: '' });
                    }}
                    className="font-medium text-blue-400 hover:text-blue-300 cursor-pointer transition-colors"
                  >
                    {isRegistering ? 'Giriş Yap' : 'Kayıt Ol'}
                  </button>
                </span>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-transparent text-white/60">veya</span>
                </div>
              </div>

              <div className="mt-6">
                <button 
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-white/20 rounded-xl shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all backdrop-blur-sm cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-google-fill text-red-400 mr-2"></i>
                  Google ile {isRegistering ? 'Hızlı Kayıt' : 'Giriş Yap'}
                </button>
              </div>
            </div>

            {/* E-posta Doğrulama Bilgilendirmesi */}
            {!isRegistering && (
              <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-start">
                  <i className="ri-mail-check-line text-blue-400 mt-0.5 mr-2 flex-shrink-0"></i>
                  <div className="text-xs text-blue-200">
                    <strong>E-posta doğrulama hatası alıyorsanız:</strong>
                    <ul className="mt-1 space-y-1">
                      <li>• E-posta kutunuzu kontrol edin</li>
                      <li>• Spam/gereksiz klasörünü kontrol edin</li>
                      <li>• Doğrulama linkine tıklayın</li>
                      <li>• Birkaç dakika bekleyip tekrar deneyin</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Terms */}
          <div className="text-center mt-6">
            <p className="text-xs text-white/50">
              {isRegistering ? 'Kayıt olarak' : 'Giriş yaparak'}{' '}
              <Link to="/terms" className="text-orange-400 hover:text-orange-300 cursor-pointer transition-colors">
                Kullanım Şartları
              </Link>{' '}
              ve{' '}
              <Link to="/privacy" className="text-orange-400 hover:text-orange-300 cursor-pointer transition-colors">
                Gizlilik Politikası
              </Link>
              'nı kabul etmiş olursunuz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
